<template>
  <div
    :id="$data.$_tabsParentId"
    ref="pdpTabsParent"
    class="pdp-tabs-parent-container"
    :class="{ 'sticky-tabs': enableSticky && triggerSticky }"
  >
    <div
      ref="tabs"
      class="d-flex container pdp-section-tab-container"
    >
      <span
        v-for="(tab, index) in sectionArr"
        :key="index"
        :data-hash="tab.hash"
        class="d-flex pdp-section-tab"
      >
        <a
          :href="tab.elementId"
          class="h3 pdp-section-tab-item d-flex align-items-center"
          :class="{ 'pdp-section-tab-item-active': activeHash === tab.hash && triggerSticky }"
          @click.prevent="onTabClick(tab.hash)"
          @keydown.enter.prevent="onTabClick(tab.hash)"
        >
          {{ tab.title }}
        </a>
      </span>
    </div>
    <button
      v-show="showLeftArrow"
      class="left-arrow"
      @click="transitionTabs('prev')"
    >
      <span class="sr-only">Scroll to left</span>
      <font-awesome-icon
        icon="chevron-left"
        height="16"
        width="16"
      />
    </button>
    <button
      v-show="showRightArrow"
      class="right-arrow"
      @click="transitionTabs('next')"
    >
      <span class="sr-only">Scroll to right</span>
      <font-awesome-icon
        icon="chevron-right"
        height="16"
        width="16"
      />
    </button>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PDPScrollToElementMixin from './mixins/PDPScrollToElementMixin';
import { DISCUSSION_TABS } from '~/constants/discussions';

const ARROW_GAP = 35;
const MAIN_NAV_BAR_HEIGHT = 80;

export default {
  components: {
    FontAwesomeIcon,
  },
  mixins: [PDPScrollToElementMixin],
  props: {
    enableSticky: {
      type: Boolean,
      default: false,
    },
    deal: {
      type: Object,
      required: true,
    },
    hasFoundersPost: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeHash: '',
      triggerSticky: false,
      showLeftArrow: false,
      showRightArrow: true,
      scrollObserver: null,
      isScrolling: false,
      scrollToHashTimeoutId: null,
    };
  },
  computed: {
    hasOverview() {
      return !!this.deal.overview_media.length;
    },
    sectionArr() {
      let arr = [];

      if (this.hasOverview) {
        arr = arr.concat([{ title: 'Overview', elementId: '#overview', hash: '#overview' }]);
      }

      // if active OR 'sold out', include plans & features tab
      if (!this.deal.stage_details.is_inactive_indefinitely
       || (this.deal.buy_button.is_ended && !this.deal.is_info)) {
        arr = arr.concat([{ title: 'Plans & features', elementId: '#pricePlans', hash: '#pricePlans' }]);
      }

      if (this.hasFoundersPost) {
        arr = arr.concat([{ title: 'From the founders', elementId: '#from-the-founders', hash: '#from-the-founders' }]);
      }

      arr = arr.concat([{ title: 'Questions', elementId: '#questions', hash: '#questions' }]);

      if (!this.deal.is_info) {
        arr = arr.concat([{ title: 'Reviews', elementId: '#reviews', hash: '#reviews' }]);
      }

      return arr;
    },
  },
  watch: {
    activeHash(newHash) {
      const isDiscussionTab = Object.values(DISCUSSION_TABS).some((tab) => `#${tab}` === newHash);

      if (isDiscussionTab) {
        this.$store.dispatch('discussion/setActiveTab', newHash);
      }
    },
  },
  mounted() {
    // jl: if we're navigating to pdp from another page, let's set scroll position to
    // the top so that we can show smooth scroll animation (only if a hash exists in url)
    if (window.location.hash) {
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 1);
    }

    // TODO: Improve to register event only when scroll exist
    this.$refs.tabs.addEventListener('scroll', this.onHorizontalScrollTabs);
    this.onHorizontalScrollTabs();

    if (this.enableSticky) {
      window.addEventListener('scroll', this.toggleStickyHandler);
      this.scrollSpy();
    }

    // jw: PDP needs to handle directing the user to elements on the page by hash in the URL, and
    //     pushing those URLS into the history. As a result, we need to handle popstate manually
    //     regardless of whether the sticky navigation is being used.
    window.addEventListener('popstate', this.onPopStateHandler);
    window.addEventListener('hashchange', this.onPopStateHandler);

    // wait until document loading process is done so we know all elements are present in the page
    window.addEventListener('load', this.onPopStateHandler);
  },
  beforeDestroy() {
    // jw: deregistering these in the opposite order than they were registered above in mounted.
    window.removeEventListener('load', this.onPopStateHandler);
    window.removeEventListener('popstate', this.onPopStateHandler);
    window.removeEventListener('hashchange', this.onPopStateHandler);

    if (this.enableSticky) {
      window.removeEventListener('scroll', this.toggleStickyHandler);
      this.scrollObserver.disconnect();
    }

    this.$refs.tabs.removeEventListener('scroll', this.onHorizontalScrollTabs);
  },
  methods: {
    isValidSectionTabHash(newHash) {
      return !!newHash && this.sectionArr.some((x) => x.hash === newHash);
    },
    onPopStateHandler() {
      // jw: only need to perform this processing if there is a hash
      if (window.location.hash) {
        this.scrollToHash(window.location.hash);
      }
    },
    scrollToHash(hash, attempts = 0) {
      // jw: if we have tried more than 20 times let's call it a day.
      if (attempts >= 20) {
        return;
      }

      // jw: next, if it corresponds to a section tab then manage that state as well:
      if (this.isValidSectionTabHash(hash)) {
        this.activeHash = hash;
        this.$nextTick(() => this.triggerHorizontalScroll);
      }

      // jw: attempt to load an element from the hash, and if one can't be found try again in 50ms
      const element = document.querySelector(hash);
      if (!element) {
        // jw: always clear any timeout that may have been registered previously. We only care about
        //     the most recent scroll.
        clearTimeout(this.scrollToHashTimeoutId);
        this.scrollToHashTimeoutId = setTimeout(() => {
          this.scrollToHashTimeoutId = null;
          this.scrollToHash(hash, attempts + 1);
        }, 50);
        return;
      }

      // jw: we have an element, so let's handle it appropriately by first triggering the scroll.
      this.$_scrollToElement(element);
    },
    toggleStickyHandler() {
      const currentYScroll = Math.round(window.pageYOffset);
      this.triggerSticky = currentYScroll > MAIN_NAV_BAR_HEIGHT;

      if (this.activeHash && this.isScrolling) {
        const { elementId } = this.sectionArr.find((x) => x.hash === this.activeHash);
        const el = document.querySelector(elementId);

        if (el.offsetTop === currentYScroll) {
          // Enable Intersection Observer to listen.
          setTimeout(() => { this.isScrolling = false; }, 500);
        }
      } else if (this.activeHash && !this.triggerSticky
        && this.enableSticky && this.lastYScroll
        && (currentYScroll - this.lastYScroll) < 0) {
        this.activeHash = '';
      }

      this.lastYScroll = currentYScroll;
    },
    onTabClick(hash) {
      // Using history instead window.location to avoid to be fired 'popstate' (re-render page)
      // and we also cannot use $route.push since this will re-render base.vue bodyAttrs class
      // and will interfere with /util/navbar-scroll.js to show/hide navbar and also with
      // this SectionTabs top position
      window.history.pushState({}, '', hash);
      this.scrollToHash(hash);
    },
    triggerHorizontalScroll() {
      if (!this.enableSticky) {
        return;
      }

      // Prevent Intersection Observer to listen while scrolling
      this.isScrolling = true;

      const tabElement = document.querySelector(`[data-hash="${this.activeHash}"]`);
      this.triggerScrollTabs(tabElement.offsetLeft);

      // Enable again Intersection Observer
      setTimeout(() => { this.isScrolling = false; }, 500);
    },
    scrollSpy() {
      this.scrollObserver = new IntersectionObserver((entries) => {
        // jw: if we are in the process of scrolling then short out.
        if (this.isScrolling) {
          return;
        }
        const intersectingSection = entries.find((x) => x.isIntersecting);

        if (intersectingSection) {
          const elementId = intersectingSection.target.id;
          this.activeHash = this.sectionArr.find((x) => x.elementId === `#${elementId}`).hash;

          const tabElement = document.querySelector(`[data-hash="${this.activeHash}"]`);
          this.triggerScrollTabs(tabElement.offsetLeft);
        } else {
          this.activeHash = '';
        }
        // jl: using negative top+bottom margins so blue nav tab transitions occur when tab section
        // content -- e.g. 'overview' -- is mostly on the page (45% or closer to top)
        // see https://codepen.io/jlundkovsky/pen/RwLwzwg for visual understanding
      }, { root: null, rootMargin: '-45% 0px -55% 0px' });

      this.sectionArr.forEach((x) => {
        const el = document.querySelector(x.elementId);
        if (el) {
          this.scrollObserver.observe(el);
        }
      });
    },
    triggerScrollTabs(newPosition) {
      // Will fire scroll event
      this.$refs.tabs.scrollTo({ left: newPosition - ARROW_GAP, behavior: 'smooth' });
    },
    onHorizontalScrollTabs() {
      // Trigger by scroll in tabs, click on tab or scroll between sections (observer intersection)
      this.toggleArrows(this.$refs.tabs.scrollLeft);
    },
    toggleArrows(currentPosition) {
      const tabsPaddingGap = 12;

      this.showLeftArrow = (currentPosition - ARROW_GAP - tabsPaddingGap) > 0;

      const { tabs = {} } = this.$refs;
      const lastChild = tabs.querySelector('.pdp-section-tab:last-child');
      const scrollRight = currentPosition + tabs.clientWidth - ARROW_GAP - tabsPaddingGap;

      this.showRightArrow = scrollRight < lastChild.offsetLeft;
    },
    transitionTabs(type) {
      const { tabs = {} } = this.$refs;

      const childrenOffset = Array.from(tabs.children).map((x) => x.offsetLeft);
      let newPositon = 0;

      if (type === 'next') {
        newPositon = childrenOffset.find((x) => x > tabs.scrollLeft + ARROW_GAP);
      } else if (type === 'prev') {
        newPositon = childrenOffset
          .sort((a, b) => b - a)
          .find((x) => x < tabs.scrollLeft - ARROW_GAP);
      }

      this.triggerScrollTabs(newPositon);
    },
  },
};
</script>

<style>
  /*
    react to navbar scroll behavior (.navbar-visible is attached to body)

    Note: 991px is the line as that is where the header is always 2 row
    while desktop it is all one row and shorter (grabbed from the
    .pdp-section-tab-container section below)
  */

  /* desktop/default or landscape tablets */
  .navbar-visible .pdp-tabs-parent-container.sticky-tabs {
    /* account for 1-row nav bar spacing */
    transform: translateY(80px) !important;
  }

  /* For new navbar desktop/default or landscape tablets */
  /* stylelint-disable-next-line selector-class-pattern */
  .with_fixed_header.navbar-visible .pdp-tabs-parent-container.sticky-tabs {
    transform: translateY(130px) !important;
  }

  /* mobile (phone and tablet) or small desktop windows */
  @media (max-width: 991px) {
    .navbar-visible .pdp-tabs-parent-container.sticky-tabs {
      /* account for 2-row nav bar spacing */
      transform: translateY(140px) !important;
    }
  }
</style>

<style lang="scss" scoped>
  .pdp-tabs-parent-container {
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(#000, 0.1);
    position: fixed;
    top: 0;
    transition: transform 0.4s ease-in-out;
    width: 100%;
    z-index: 1020;
  }

  .pdp-section-tab-item {
    border-bottom: 4px solid #fff;
    color: #434d59;
    display: inline-block;
    font-size: 20px;
    line-height: 36px;
    margin: 0 20px;
    max-width: fit-content;
    text-decoration: none !important;
    transition: border 100ms;
  }

  .pdp-section-tab-item:hover {
    color: #1b1b1b;
  }

  .pdp-section-tab-container {
    height: 76px;
    overflow-x: auto;
    padding-top: 9px;
    position: relative;
    white-space: nowrap;
    overflow: -moz-scrollbars-none;
    -ms-overflow-style: none;

    .pdp-section-tab:first-child .pdp-section-tab-item {
      margin-left: 0;
    }

    .pdp-section-tab:last-child .pdp-section-tab-item {
      margin-right: 0;
    }
  }

  .pdp-section-tab-item-active {
    border-color: #0447c0;
    color: #1b1b1b;
  }

  .left-arrow,
  .right-arrow {
    background-color: #fff;
    border: 0;
    display: none;
    height: 100%;
    opacity: 0.9;
    outline: 0;
    position: absolute;
    top: 0;
    width: 50px;
  }

  .svg-inline--fa {
    vertical-align: -0.3em;
  }

  @media (min-width: 768px) {
    .pdp-section-tab-item {
      margin: 0 32px;
    }
  }

  @media (max-width: 991px) {
    .pdp-section-tab-container {
      -ms-overflow-style: none;  /* IE and Edge */
      scrollbar-width: none;  /* Firefox */
    }

    /* Hide horizontal scrollbar */
    .pdp-section-tab-container::-webkit-scrollbar {
      display: none; /* Chrome, safari */
    }

    .right-arrow,
    .left-arrow {
      display: inline-block;
    }

    .right-arrow {
      left: calc(100vw - 50px);
    }

    .left-arrow {
      left: 0;
    }
  }

  @media (max-width: 767px) {
    .pdp-section-tab-container {
      align-content: unset;
      justify-content: unset !important;
      overflow-x: scroll;
      overflow: -moz-scrollbars-none;
      -ms-overflow-style: none;  /* IE and Edge */
      scrollbar-width: none;  /* Firefox */
    }

    .pdp-section-tab-container::-webkit-scrollbar {
      width: 0 !important;
      display: none; /* Chrome, safari */
    }
  }
</style>
